import { useState, useCallback, useRef } from 'react'
import api from '../services/api'

interface Notification {
  id: string
  type: 'agent_activity' | 'clinical_alert' | 'content_change'
  message: string
  timestamp: number
  read: boolean
}

export function useNotifications() {
  const [notifications, setNotifications] = useState<Notification[]>([])
  const workspaceIdRef = useRef<string | null>(null)

  /** Load persisted notifications from hydration data.
   *  Always clears previous workspace notifications first. */
  const loadFromHydration = useCallback((hydrationNotifs: any[], wsId: string) => {
    workspaceIdRef.current = wsId
    if (!hydrationNotifs?.length) {
      setNotifications([])
      return
    }
    setNotifications(hydrationNotifs.map((n: any) => ({
      id: n.id,
      type: n.type,
      message: n.message,
      timestamp: n.timestamp ? new Date(n.timestamp).getTime() : Date.now(),
      read: n.read ?? false,
    })))
  }, [])

  /** Add a notification — persists to DB if workspace is set */
  const addNotification = useCallback((type: Notification['type'], message: string) => {
    const id = `notif-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
    const notif: Notification = { id, type, message, timestamp: Date.now(), read: false }
    setNotifications(prev => [notif, ...prev].slice(0, 50))

    // Fire-and-forget persist to DB
    if (workspaceIdRef.current) {
      api.post(`/api/v1/workspaces/${workspaceIdRef.current}/notifications`, { type, message })
        .then(resp => {
          // Replace temp id with DB id
          if (resp.data?.id) {
            setNotifications(prev => prev.map(n => n.id === id ? { ...n, id: resp.data.id } : n))
          }
        })
        .catch((err) => { console.warn('[useNotifications] Failed to persist notification to DB:', err) })
    }
  }, [])

  const markAsRead = useCallback((id: string) => {
    setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n))
  }, [])

  const markAllAsRead = useCallback(() => {
    setNotifications(prev => prev.map(n => ({ ...n, read: true })))
    if (workspaceIdRef.current) {
      api.post(`/api/v1/workspaces/${workspaceIdRef.current}/notifications/read`).catch((err) => { console.warn('[useNotifications] Failed to mark all as read:', err) })
    }
  }, [])

  /** Clear all notifications — deletes from DB */
  const clearAll = useCallback(() => {
    setNotifications([])
    if (workspaceIdRef.current) {
      api.delete(`/api/v1/workspaces/${workspaceIdRef.current}/notifications`).catch((err) => { console.warn('[useNotifications] Failed to clear notifications from DB:', err) })
    }
  }, [])

  const unreadCount = notifications.filter(n => !n.read).length

  return { notifications, addNotification, markAsRead, markAllAsRead, clearAll, loadFromHydration, unreadCount }
}
